import { useState, useEffect } from 'react';
import { Button, Input } from '../ui';
import { Avatar } from '../Avatar';
import { EmptyState } from '../EmptyState';
import { useToast } from '../Toast';
import {
  fetchMembers,
  setMemberFounding,
  setMemberRole,
  updateMemberProfile,
  generateSignInLink,
  sendMemberInvite,
  deleteMember,
  type MemberRow,
} from '../../lib/members-admin-queries';
import {
  addAllowedEmail,
  removeAllowedEmail,
  setAllowedEmailFounding,
} from '../../lib/admin-queries';
import type { Role } from '../../lib/types';

// ===========================================================================
// Members panel - search, filter, toggle founding status, manage roles, etc.
// Lazy-loads on first activation. Owns the admin/members data; the original
// Testers tab logic is fully replaced here.
// ===========================================================================
export function MembersPanel({ active, me, onComposeTo }: { active: boolean; me: { id: string; role: Role }; onComposeTo: (recipients: { email: string; display_name: string | null }[]) => void }) {
  const toast = useToast();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [foundingOnly, setFoundingOnly] = useState(false);

  // Bulk-email selection - a Set of lowercased emails so it survives filtering
  // and pagination (selecting on page 1 then sending stays intact on page 2).
  const [selected, setSelected] = useState<Set<string>>(new Set());

  function toggleSelect(email: string) {
    const key = email.toLowerCase();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  // Pagination - client-side; server returns everything sorted by recent activity.
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, foundingOnly]);

  // Add form
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [addFounding, setAddFounding] = useState(false);
  const [addBusy, setAddBusy] = useState(false);

  // Per-row state
  const [savingId, setSavingId] = useState<string | null>(null);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);

  // Role / profile / sign-in-link per-row state.
  const [roleSavingId, setRoleSavingId] = useState<string | null>(null);
  const [profileEditId, setProfileEditId] = useState<string | null>(null);
  const [pName, setPName] = useState('');
  const [pHandle, setPHandle] = useState('');
  const [profileSavingId, setProfileSavingId] = useState<string | null>(null);
  const [linkBusyId, setLinkBusyId] = useState<string | null>(null);
  // Which row's actions menu is open (one at a time). Keyed by user_id.
  const [actionsMenuId, setActionsMenuId] = useState<string | null>(null);
  // Per-email busy flag for the "Send invite" / "Resend invite" button on
  // rows that haven't signed in yet. Keyed by email since these rows have
  // no user_id.
  const [invitingEmail, setInvitingEmail] = useState<string | null>(null);

  // Hard-delete confirmation modal. Holds the row being deleted; the admin must
  // type the email to confirm. reinvite re-adds a fresh whitelist row.
  const [deleteTarget, setDeleteTarget] = useState<MemberRow | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteReinvite, setDeleteReinvite] = useState(false);
  const [deleteReinviteFounding, setDeleteReinviteFounding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const ROLE_LABEL: Record<Role, string> = {
    user: 'Member',
    admin: 'Admin',
    developer: 'Developer',
  };

  // Which roles can `me` assign to this row? null = no role control
  // (not signed in, the caller themselves, or out of the caller's reach).
  function roleOptionsFor(target: MemberRow): Role[] | null {
    if (!target.user_id) return null;
    if (target.user_id === me.id) return null;
    if (me.role === 'developer') return ['user', 'admin', 'developer'];
    if (me.role === 'admin') {
      if (target.role === 'developer') return null; // admins don't manage devs
      return ['user', 'admin'];
    }
    return null;
  }

  async function changeRole(member: MemberRow, next: Role) {
    if (!member.user_id || next === member.role) return;
    const who = member.display_name || member.email;
    const ok = window.confirm(
      `Change ${who}'s role from ${ROLE_LABEL[(member.role ?? 'user') as Role]} ` +
      `to ${ROLE_LABEL[next]}?\n\n` +
      (next === 'admin' || next === 'developer'
        ? `They'll gain ${next === 'developer' ? 'developer' : 'admin'} access to /admin ` +
          `(and free Club access).\n\n`
        : `They'll lose staff access and fall back to a normal member.\n\n`) +
      `Takes effect on their next login, or within ~1 hour as their session refreshes.`,
    );
    if (!ok) return;
    setRoleSavingId(member.user_id);
    try {
      const res = await setMemberRole({ userId: member.user_id, role: next });
      toast.success(`${who} is now ${ROLE_LABEL[res.role]}. ${res.note}`);
      await refresh();
    } catch (err: any) {
      toast.error(friendlyRoleError(err?.message));
    } finally {
      setRoleSavingId(null);
    }
  }

  function friendlyRoleError(msg?: string): string {
    switch (msg) {
      case 'cannot_change_own_role':
        return "You can't change your own role.";
      case 'developer_role_requires_developer':
        return 'Only a developer can grant or change the developer role.';
      case 'cannot_remove_last_admin':
        return "Can't demote the last admin/developer, promote someone else first.";
      case 'forbidden':
        return 'You do not have permission for that.';
      default:
        return msg || 'Role change failed.';
    }
  }

  function startProfileEdit(member: MemberRow) {
    if (!member.user_id) return;
    setProfileEditId(member.user_id);
    setPName(member.display_name ?? '');
    setPHandle(member.handle ?? '');
  }

  function cancelProfileEdit() {
    setProfileEditId(null);
    setPName('');
    setPHandle('');
  }

  async function saveProfile(member: MemberRow) {
    if (!member.user_id) return;
    setProfileSavingId(member.user_id);
    try {
      await updateMemberProfile({
        userId: member.user_id,
        display_name: pName.trim() || null,
        handle: pHandle.trim() ? pHandle.trim() : null,
      });
      toast.success('Profile updated.');
      cancelProfileEdit();
      await refresh();
    } catch (err: any) {
      const m = err?.message ?? '';
      toast.error(
        /handle_taken/.test(m)
          ? 'That @handle is already taken.'
          : /invalid_handle/.test(m)
            ? 'Handle must be 2–30 chars: a–z, 0–9, underscore.'
            : m || 'Update failed.',
      );
    } finally {
      setProfileSavingId(null);
    }
  }

  async function sendInvite(member: MemberRow) {
    const isResend = (member.invite_count ?? 0) > 0;
    const who = member.display_name || member.email;
    const ok = window.confirm(
      (isResend
        ? `Resend the invite email to ${who} (${member.email})?\n\n`
        : `Send an invite email to ${who} (${member.email})?\n\n`) +
      `They'll get a branded email with a single-use sign-in link. The button on ` +
      `this row will switch to "Resend invite" until they actually log in, then ` +
      `disappear.`,
    );
    if (!ok) return;
    setInvitingEmail(member.email);
    try {
      const res = await sendMemberInvite(member.email);
      toast.success(
        res.warn
          ? `${isResend ? 'Resent' : 'Sent'}: ${res.warn}`
          : `${isResend ? 'Resent' : 'Invite sent'} to ${member.email}.`,
      );
      // Optimistic local update so the button label flips to "Resend"
      // immediately; refresh below picks up the canonical timestamp.
      setMembers((prev) =>
        prev.map((m) =>
          m.email.toLowerCase() === member.email.toLowerCase()
            ? { ...m, last_invited_at: new Date().toISOString(), invite_count: res.invite_count }
            : m,
        ),
      );
      // Quietly re-pull in the background to catch any server-side changes
      // (e.g. status flip) without a visible loading state.
      void refresh();
    } catch (err: any) {
      const msg = err?.message ?? 'Invite failed.';
      toast.error(
        /email_not_configured/.test(msg)
          ? 'Email not set up yet: RESEND_API_KEY / INVITE_FROM_EMAIL must be configured on the Cloudflare Pages project.'
          : msg,
      );
    } finally {
      setInvitingEmail(null);
    }
  }

  async function copySignInLink(member: MemberRow) {
    if (!member.user_id) return;
    setLinkBusyId(member.user_id);
    try {
      const { link } = await generateSignInLink(member.user_id);
      try {
        await navigator.clipboard.writeText(link);
        toast.success('Single-use sign-in link copied, share only with them.');
      } catch {
        window.prompt('Copy this single-use sign-in link:', link);
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not generate a link.');
    } finally {
      setLinkBusyId(null);
    }
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const mems = await fetchMembers();
      setMembers(mems);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load members.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Filter
  const filtered = members.filter((m) => {
    if (search) {
      const q = search.trim().toLowerCase();
      const hay = `${m.email} ${m.display_name ?? ''} ${m.handle ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (foundingOnly && !m.is_founding_member) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const visible = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  // Select-all operates on the whole filtered set (not just the visible page).
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((m) => selected.has(m.email.toLowerCase()));
  function toggleSelectAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (filtered.every((m) => next.has(m.email.toLowerCase()))) {
        filtered.forEach((m) => next.delete(m.email.toLowerCase()));
      } else {
        filtered.forEach((m) => next.add(m.email.toLowerCase()));
      }
      return next;
    });
  }
  const selectedRecipients = members
    .filter((m) => selected.has(m.email.toLowerCase()))
    .map((m) => ({ email: m.email, display_name: m.display_name }));

  // Format ISO date → relative time + tooltip with full date.
  function fmtTimestamp(iso: string | null): { label: string; title: string } {
    if (!iso) return { label: ' - ', title: '' };
    const d = new Date(iso);
    const now = Date.now();
    const diffSec = Math.floor((now - d.getTime()) / 1000);
    let label = '';
    if (diffSec < 60) label = 'just now';
    else if (diffSec < 3600) label = `${Math.floor(diffSec / 60)}m ago`;
    else if (diffSec < 86400) label = `${Math.floor(diffSec / 3600)}h ago`;
    else if (diffSec < 86400 * 30) label = `${Math.floor(diffSec / 86400)}d ago`;
    else label = d.toLocaleDateString();
    return { label, title: d.toLocaleString() };
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    // A member is keyed by their email - never create a row without a valid one
    // (a blank/garbled email is what created the orphaned, login-less record).
    const email = addEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email address.');
      return;
    }
    setAddBusy(true);
    try {
      await addAllowedEmail({
        email,
        display_name: addName.trim() || null,
        is_founding_member: addFounding,
      });
      toast.success('Member added.');
      setAddEmail('');
      setAddName('');
      setAddFounding(false);
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to add.');
    } finally {
      setAddBusy(false);
    }
  }

  async function handleRemoveFromWhitelist(email: string) {
    const ok = window.confirm(
      `Cancel the invite for ${email}?\n\n` +
      `Removes them from the invite list. It does NOT delete an existing account. Continue?`,
    );
    if (!ok) return;
    setRemovingEmail(email);
    try {
      await removeAllowedEmail(email);
      toast.success('Removed from whitelist.');
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Remove failed.');
    } finally {
      setRemovingEmail(null);
    }
  }

  // Toggle a member's founding status. true grants comped Club access
  // (bypasses the paywall); false revokes it (paid subs untouched). Both
  // directions confirm inline since they change billing/access.
  async function toggleFounding(member: MemberRow, next: boolean) {
    // Not-yet-signed-in rows have no profile, so we set the seed value on the
    // whitelist instead; it's copied onto their profile at first login.
    if (!member.user_id) {
      setSavingId(member.email);
      try {
        await setAllowedEmailFounding(member.email, next);
        toast.success(
          next
            ? "Marked as founding: they'll get free Club access when they first sign in."
            : "Founding removed: they'll go through the paywall when they sign in.",
        );
        await refresh();
      } catch (err: any) {
        toast.error(err?.message ?? 'Update failed.');
      } finally {
        setSavingId(null);
      }
      return;
    }
    const who = member.display_name || member.email;
    const ok = window.confirm(
      next
        ? `Make ${who} a founding member?\n\n` +
          `Gives free Club access + the Founding badge. Takes effect within ~1 hr (instantly on re-login).`
        : `Remove founding status from ${who}?\n\n` +
          `Ends their free Club access (any paid subscription stays). Account, posts and comments are kept.`,
    );
    if (!ok) return;
    setSavingId(member.user_id);
    try {
      await setMemberFounding({ userId: member.user_id, isFounding: next });
      toast.success(
        next
          ? `${who} is now a founding member.`
          : `Founding status removed. They'll hit the paywall on next session refresh.`,
      );
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Update failed.');
    } finally {
      setSavingId(null);
    }
  }

  // Open the hard-delete confirmation modal for a signed-in member.
  function openDelete(member: MemberRow) {
    if (!member.user_id) return;
    setDeleteTarget(member);
    setDeleteConfirm('');
    setDeleteReinvite(false);
    setDeleteReinviteFounding(false);
  }

  function closeDelete() {
    setDeleteTarget(null);
    setDeleteConfirm('');
    setDeleteReinvite(false);
    setDeleteReinviteFounding(false);
  }

  // Permanently delete the member. Their auth login + profile go; their posts/
  // uploads/comments are reassigned to "Deleted member". Guarded server-side.
  async function confirmDelete() {
    const member = deleteTarget;
    if (!member?.user_id) return;
    setDeletingId(member.user_id);
    try {
      const res = await deleteMember({
        userId: member.user_id,
        reinvite: deleteReinvite,
        reinviteFounding: deleteReinvite && deleteReinviteFounding,
      });
      toast.success(
        res.reinvited
          ? `${member.email} deleted and re-invited for a fresh start.`
          : `${member.display_name || member.email} permanently deleted.`,
      );
      closeDelete();
      await refresh();
    } catch (err: any) {
      toast.error(friendlyDeleteError(err?.message));
    } finally {
      setDeletingId(null);
    }
  }

  function friendlyDeleteError(msg?: string): string {
    switch (msg) {
      case 'cannot_delete_self':
        return "You can't delete your own account.";
      case 'cannot_delete_last_admin':
        return "Can't delete the last admin/developer, promote someone else first.";
      case 'developer_required_for_staff':
        return 'Only a developer can delete another admin/developer.';
      case 'ghost_member_missing':
        return 'The "Deleted member" placeholder is missing: apply migration 052 first.';
      case 'profile_not_found':
        return 'That member no longer exists.';
      default:
        return msg || 'Delete failed.';
    }
  }

  if (!loaded && loading) {
    return (
      <>
        <div className="admin__panel-head"><h2>Members</h2></div>
        <p className="admin__note">Loading…</p>
      </>
    );
  }

  return (
    <>
      <div className="admin__panel-head">
        <h2>Members</h2>
        <span className="admin__count">{filtered.length} of {members.length}</span>
      </div>

      {error && (
        <div className="admin__error">
          <p className="kicker">Problem</p>
          <p>{error}</p>
        </div>
      )}

      {/* Add form ----------------------------------------------------- */}
      <form className="admin__add" onSubmit={handleAdd}>
        <div className="admin__add-row">
          <label>
            <span>Email</span>
            <Input
              type="email"
              required
              placeholder="name@example.com"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              disabled={addBusy}
            />
          </label>
          <label>
            <span>Display name</span>
            <Input
              type="text"
              placeholder="Optional"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              disabled={addBusy}
            />
          </label>
          <div className="admin__add-actions">
            <Button type="submit" variant="primary" size="sm" disabled={addBusy || !addEmail}>
              {addBusy ? '…' : 'Add member'}
            </Button>
          </div>
        </div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={addFounding}
              onChange={(e) => setAddFounding(e.target.checked)}
              disabled={addBusy}
            />
            <span className="onb__label" style={{ margin: 0 }}>Founding member</span>
          </label>
          <small style={{ display: 'block', marginTop: 6, color: 'var(--text-soft)' }}>
            Founding members get free Club access (a comped subscription) on first sign-in and the
            Founding Member badge. Leave off and they'll go through the paywall.
          </small>
        </div>
      </form>

      {/* Filters ------------------------------------------------------ */}
      <div className="admin__add" style={{ marginTop: 'var(--space-4)' }}>
        <div className="admin__add-row">
          <label style={{ flex: 1 }}>
            <span>Search</span>
            <Input
              type="search"
              placeholder="Email, name, or @handle"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="admin__add-actions">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={refresh}
              disabled={loading}
            >
              {loading ? '…' : 'Refresh'}
            </Button>
          </div>
        </div>
        <div style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: 'var(--text-soft)', fontSize: '0.9em' }}>Filter:</span>
          <Button
            type="button"
            size="sm"
            variant={foundingOnly ? 'secondary' : 'ghost'}
            style={{ fontSize: '0.85em' }}
            onClick={() => setFoundingOnly((v) => !v)}
            aria-pressed={foundingOnly}
          >
            {foundingOnly ? '✓ ' : ''}Founding members only
          </Button>
        </div>
      </div>

      {/* Bulk-email selection toolbar -------------------------------- */}
      <div style={{
        display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap',
        margin: 'var(--space-3) 0', padding: '10px 14px',
        background: 'var(--bone, #f6f4ef)', border: '1px solid var(--smoke)', borderRadius: 'var(--radius-sm)',
      }}>
        {selected.size > 0 ? (
          <>
            <strong>{selected.size} selected</strong>
            <Button type="button" variant="primary" size="sm" onClick={() => onComposeTo(selectedRecipients)}>
              Email selected
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </>
        ) : (
          <>
            <span style={{ color: 'var(--text-soft)' }}>Tick members to email them</span>
            <Button type="button" variant="primary" size="sm" disabled onClick={() => onComposeTo(selectedRecipients)}>
              Email selected
            </Button>
          </>
        )}
      </div>

      {/* Table -------------------------------------------------------- */}
      {filtered.length === 0 ? (
        <EmptyState
          tone="compact"
          align="start"
          eyebrow="No matches"
          headline={members.length === 0 ? 'No members yet.' : 'No one matches that filter.'}
          body={members.length === 0 ? 'Add one above to get started.' : 'Adjust the search or filter to see more.'}
        />
      ) : (
        <div className="admin__table-wrap">
        <table className="admin__table">
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                  aria-label="Select all matching members"
                  title="Select all matching members"
                />
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Role</th>
              <th>Founding</th>
              <th>First login</th>
              <th>Last login</th>
              <th aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((m) => {
              const pEditing = !!m.user_id && m.user_id === profileEditId;
              const isSelf = !!m.user_id && m.user_id === me.id;
              const roleOpts = roleOptionsFor(m);
              const first = fmtTimestamp(m.first_login_at);
              const last = fmtTimestamp(m.last_sign_in_at);
              return (
                <tr key={m.email + (m.user_id ?? '')}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(m.email.toLowerCase())}
                      onChange={() => toggleSelect(m.email)}
                      aria-label={`Select ${m.display_name || m.email}`}
                    />
                  </td>
                  <td className="admin__cell-name">
                    {pEditing ? (
                      <div className="admin__profile-edit">
                        <Input
                          type="text"
                          value={pName}
                          placeholder="Display name"
                          onChange={(e) => setPName(e.target.value)}
                          disabled={profileSavingId === m.user_id}
                          aria-label="Display name"
                        />
                        <Input
                          type="text"
                          value={pHandle}
                          placeholder="handle"
                          onChange={(e) => setPHandle(e.target.value)}
                          disabled={profileSavingId === m.user_id}
                          aria-label="Handle"
                          spellCheck={false}
                        />
                      </div>
                    ) : (
                      <span className="admin__person">
                        <Avatar
                          name={m.display_name || m.email}
                          size="sm"
                          founding={m.is_founding_member}
                        />
                        <span>
                          {m.display_name || ' - '}
                          {isSelf && (
                            <span className="pill pill--you" style={{ marginLeft: 6 }}>you</span>
                          )}
                          {m.handle && (
                            <span style={{ color: 'var(--text-soft)', display: 'block', fontSize: '0.85em' }}>
                              @{m.handle}
                            </span>
                          )}
                        </span>
                      </span>
                    )}
                  </td>
                  <td className="admin__cell-email"><code title={m.email}>{m.email}</code></td>
                  <td>
                    {m.status ? (
                      <span className={`pill pill--${m.status}`}>{m.status}</span>
                    ) : (
                      <span style={{ color: 'var(--text-soft)' }}> - </span>
                    )}
                  </td>
                  <td>
                    {roleOpts ? (
                      <select
                        className="admin__select admin__role-select"
                        value={(m.role ?? 'user') as Role}
                        disabled={roleSavingId === m.user_id}
                        onChange={(e) => changeRole(m, e.target.value as Role)}
                        aria-label={`Role for ${m.display_name || m.email}`}
                      >
                        {roleOpts.map((r) => (
                          <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                        ))}
                        {/* Keep the current role visible even if it's outside
                            the options the caller can assign (e.g. developer
                            shown to an admin as read-only). */}
                        {!roleOpts.includes((m.role ?? 'user') as Role) && (
                          <option value={(m.role ?? 'user') as Role}>
                            {ROLE_LABEL[(m.role ?? 'user') as Role]}
                          </option>
                        )}
                      </select>
                    ) : (
                      <span
                        className="pill pill--role"
                        title={isSelf ? "You can't change your own role." : undefined}
                      >
                        {ROLE_LABEL[(m.role ?? 'user') as Role]}
                      </span>
                    )}
                  </td>
                  <td>
                    {m.is_founding_member ? (
                      <span
                        className="pill pill--badge"
                        style={{ ['--chip']: 'var(--accent)' } as React.CSSProperties}
                        title="Founding member, comped Club access."
                      >
                        Founding
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-soft)' }}> - </span>
                    )}
                  </td>
                  <td className="admin__cell-time" title={first.title}>{first.label}</td>
                  <td className="admin__cell-time" title={last.title}>{last.label}</td>
                  <td className="admin__row-actions">
                    {pEditing ? (
                      <>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={() => saveProfile(m)}
                          disabled={profileSavingId === m.user_id}
                        >
                          {profileSavingId === m.user_id ? '…' : 'Save'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={cancelProfileEdit}
                          disabled={profileSavingId === m.user_id}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : !m.user_id ? (
                      (() => {
                        const invited = fmtTimestamp(m.last_invited_at);
                        const wasInvited = !!m.last_invited_at;
                        return (
                          <>
                            <Button
                              type="button"
                              variant="primary"
                              size="sm"
                              onClick={() => sendInvite(m)}
                              disabled={invitingEmail === m.email}
                              title={
                                wasInvited
                                  ? `Last sent ${invited.title}. Hidden once they sign in.`
                                  : 'Email a branded single-use sign-in link.'
                              }
                            >
                              {invitingEmail === m.email
                                ? '…'
                                : wasInvited
                                  ? `Resend invite · ${invited.label}`
                                  : 'Send invite'}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleFounding(m, !m.is_founding_member)}
                              disabled={savingId === m.email}
                              title={
                                m.is_founding_member
                                  ? "Remove founding seed: they'll go through the paywall at first sign-in."
                                  : "Mark founding: they'll get free Club access at first sign-in."
                              }
                            >
                              {savingId === m.email
                                ? '…'
                                : m.is_founding_member
                                  ? 'Unmark founding'
                                  : 'Mark founding'}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="admin__remove"
                              onClick={() => handleRemoveFromWhitelist(m.email)}
                              disabled={removingEmail === m.email}
                              title="Remove them from the invite list. Doesn't affect an existing account."
                            >
                              {removingEmail === m.email ? '…' : 'Cancel invite'}
                            </Button>
                          </>
                        );
                      })()
                    ) : actionsMenuId === m.user_id ? (
                      <div className="admin__menu">
                        <div className="admin__menu-list" role="menu">
                          <Button
                            type="button"
                            role="menuitem"
                            variant="ghost"
                            size="sm"
                            onClick={() => { setActionsMenuId(null); toggleFounding(m, !m.is_founding_member); }}
                            disabled={savingId === m.user_id}
                            title={
                              m.is_founding_member
                                ? 'Cancel comped access. They hit the paywall.'
                                : 'Grant free comped Club access + the Founding Member badge.'
                            }
                          >
                            {m.is_founding_member ? 'Remove founding' : 'Make founding member'}
                          </Button>
                          <Button
                            type="button"
                            role="menuitem"
                            variant="ghost"
                            size="sm"
                            onClick={() => { setActionsMenuId(null); startProfileEdit(m); }}
                          >
                            Edit profile
                          </Button>
                          <Button
                            type="button"
                            role="menuitem"
                            variant="ghost"
                            size="sm"
                            onClick={() => { setActionsMenuId(null); copySignInLink(m); }}
                            disabled={linkBusyId === m.user_id}
                            title="Generate a single-use magic sign-in link to hand them."
                          >
                            {linkBusyId === m.user_id ? '…' : 'Copy sign-in link'}
                          </Button>
                          <Button
                            type="button"
                            role="menuitem"
                            variant="ghost"
                            size="sm"
                            className="admin__remove"
                            onClick={() => { setActionsMenuId(null); openDelete(m); }}
                            title="Permanently delete their account. Posts/comments are kept under 'Deleted member'."
                          >
                            Delete user…
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setActionsMenuId(null)}
                          >
                            Close
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="admin__menu">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="admin__menu-toggle"
                          aria-haspopup="menu"
                          aria-expanded={false}
                          onClick={() => setActionsMenuId(m.user_id)}
                        >
                          Actions
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {filtered.length > PAGE_SIZE && (
        <div
          className="admin__toggle-row"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--space-3)' }}
        >
          <span style={{ color: 'var(--text-soft)', fontSize: '0.9em' }}>
            Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              ← Prev
            </Button>
            <span style={{ color: 'var(--text-soft)', fontSize: '0.9em' }}>
              Page {safePage} of {totalPages}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      <p className="admin__note" style={{ marginTop: 'var(--space-4)' }}>
        <strong>Send invite</strong> emails a member who hasn't signed in yet a branded
        single-use link, and the button reappears as <strong>Resend invite</strong> until they
        log in, then disappears.
        <strong> Role</strong> promotes/demotes staff: Admin and Developer reach <code>/admin</code>.
        It takes effect on their next login (or within ~1 hr as their session refreshes); you can't
        change your own role, and only a Developer can manage the Developer role.
        <strong> Edit profile</strong> sets their display name / @handle.
        <strong> Copy sign-in link</strong> makes a single-use magic link to onboard the team.
        <strong> Make / Remove founding member</strong> grants or revokes free comped Club access
        (bypasses the paywall) and the Founding Member badge, taking effect on their next session refresh.
        For someone who hasn't signed in yet, <strong>Mark founding</strong> sets the same thing on
        their invite so it applies the moment they first log in.
        <strong> Cancel invite</strong> (shown only before someone signs in) just clears their
        pending invite row - it never deletes an account.
        <strong> Delete user…</strong> is the only action that removes a signed-in member: it
        permanently deletes their login and profile, keeping their posts and comments under an
        anonymous “Deleted member”. Optionally re-invite them in the same step for a clean restart.
      </p>

      {deleteTarget && (
        <div
          className="admin__modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Delete user"
          onClick={(e) => { if (e.target === e.currentTarget && deletingId === null) closeDelete(); }}
        >
          <div className="admin__modal">
            <h3 style={{ marginTop: 0 }}>Delete this member?</h3>
            <p style={{ color: 'var(--text-soft)' }}>
              This permanently deletes{' '}
              <strong>{deleteTarget.display_name || deleteTarget.email}</strong>{' '}
              ({deleteTarget.email}): their login, profile, subscription and personal data.
              Their posts, uploads and comments are kept but reassigned to an anonymous
              “Deleted member”. <strong>This cannot be undone.</strong>
            </p>

            <label style={{ display: 'block', marginTop: 'var(--space-3)' }}>
              <span className="onb__label">Type their email to confirm</span>
              <Input
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={deleteTarget.email}
                autoComplete="off"
                style={{ width: '100%' }}
              />
            </label>

            <label className="onb__check" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', marginTop: 'var(--space-3)' }}>
              <input
                type="checkbox"
                checked={deleteReinvite}
                onChange={(e) => setDeleteReinvite(e.target.checked)}
              />
              <span className="onb__label" style={{ margin: 0 }}>
                Re-invite this email afterwards (fresh start)
              </span>
            </label>
            {deleteReinvite && (
              <label className="onb__check" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', marginTop: 'var(--space-2)', marginLeft: 'var(--space-5)' }}>
                <input
                  type="checkbox"
                  checked={deleteReinviteFounding}
                  onChange={(e) => setDeleteReinviteFounding(e.target.checked)}
                />
                <span className="onb__label" style={{ margin: 0 }}>…as a founding member</span>
              </label>
            )}

            <div className="admin__row-actions" style={{ marginTop: 'var(--space-5)', gap: 'var(--space-3)' }}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={closeDelete}
                disabled={deletingId !== null}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="admin__remove"
                onClick={confirmDelete}
                disabled={
                  deletingId !== null ||
                  deleteConfirm.trim().toLowerCase() !== deleteTarget.email.toLowerCase()
                }
              >
                {deletingId !== null ? 'Deleting…' : 'Delete permanently'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
